import React from 'react';
import { Control, Controller, FieldPath } from 'react-hook-form';
import { Input, Select } from '@lemon-io/ui-kit';
import { SxProps, Theme } from '@mui/material';

import { AccountFormValues } from './account-step.schema';

interface FormFieldProps {
  name: FieldPath<AccountFormValues>;
  control: Control<AccountFormValues>;
  label: string;
  errorMessage?: string;
  dataTestId: string;
  required?: boolean;
}

const capitalizeFirst = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

export const FormTextField: React.FC<
  FormFieldProps & {
    sx?: SxProps<Theme>;
    capitalize?: boolean;
    transform?: (value: string) => string;
  }
> = ({
  name,
  control,
  label,
  errorMessage,
  dataTestId,
  required,
  sx,
  capitalize,
  transform,
}) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <Input
        label={label}
        size="medium"
        variant="standard"
        required={required}
        sx={sx}
        value={field.value ?? ''}
        onChange={value => {
          const next = transform ? transform(value) : value;
          field.onChange(capitalize ? capitalizeFirst(next) : next);
        }}
        error={errorMessage}
        dataTestId={dataTestId}
      />
    )}
  />
);

export const FormSelectField: React.FC<
  FormFieldProps & { options: React.ComponentProps<typeof Select>['options'] }
> = ({ name, control, label, errorMessage, dataTestId, required, options }) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <Select
        label={label}
        size="medium"
        variant="standard"
        required={required}
        options={options}
        value={field.value ?? ''}
        onChange={field.onChange}
        error={Boolean(errorMessage)}
        helperText={errorMessage}
        dataTestId={dataTestId}
      />
    )}
  />
);
